/**
 * Legal-entity context — supplies the active workspace scope to every
 * page.
 *
 * M21.5 adds a "scope" concept on top of the original single-LE
 * resolution:
 *
 *   - `scope` is `"__all__"` (the ALL_ENTITIES sentinel — the
 *     holding-company / multi-subsidiary view) OR a concrete
 *     LegalEntity id.
 *   - `legalEntityId` is ALWAYS a concrete id — when the scope is
 *     "all", it resolves to the first entity. List pages whose API
 *     endpoints require a concrete `legal_entity_id` keep working
 *     unchanged; Mission Control + the module landing pages, whose
 *     endpoints accept an optional LE, read `scope` instead and pass
 *     nothing when it's "all".
 *
 * Resolution order for `scope` (first wins):
 *   1. `?le=<uuid>` URL query param — deep links still pin a specific
 *      LE regardless of the saved preference.
 *   2. The server-side per-user preference (`GET /api/me/preferences`)
 *      — this is the source of truth; it's what the top-bar switcher
 *      writes to.
 *   3. The scope persisted in localStorage from a prior visit — a
 *      fast-path cache so there's no flash before the server responds.
 *   4. ALL_ENTITIES when the tenant has >1 LE; the single LE's id
 *      when it has exactly one.
 *
 * `setScope(scope)` updates the context, writes localStorage, and
 * PUTs to `/api/me/preferences` so the choice follows the user
 * across devices.
 *
 * Pages call `useLegalEntityId()` for a concrete id, or
 * `useLegalEntityScope()` for the `"__all__" | <id>` scope, or
 * `useLegalEntityContext()` for the full state.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { api, ApiError } from "./api";
import type { LegalEntityResponse } from "./api";

/**
 * Retry policy for the workspace-bootstrap queries. A 4xx is terminal
 * (un-provisioned tenant, auth) — don't retry it. Network errors and
 * 5xx are transient (typically a backend redeploy bouncing the API);
 * retry a few times with backoff so a deploy-window blip is invisible
 * instead of surfacing a hard "Missing ?le=" error on every page.
 */
function _retryTransient(failureCount: number, error: unknown): boolean {
  if (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 4;
}

const _LS_KEY = "solvait.legalEntityScope";

/** Scope sentinel: the holding-company / multi-subsidiary view. */
export const ALL_ENTITIES = "__all__";

interface LegalEntityContextValue {
  /** Active scope: ALL_ENTITIES or a concrete LegalEntity id. */
  scope: string;
  /** True when the active scope is ALL_ENTITIES — the "I'm
   *  intentionally cross-LE" sentinel. Useful for callers that
   *  need to distinguish "all entities" from "this specific LE"
   *  even though `legalEntityId` is still a concrete id. */
  isAllScope: boolean;
  /**
   * A concrete LegalEntity id — equals `scope` when a specific LE is
   * selected, or the first entity's id when scope is ALL_ENTITIES.
   * Empty string on tenants with no LEs. List pages use this.
   */
  legalEntityId: string;
  /** Full list for the switcher. Empty while loading / on no-LE tenants. */
  legalEntities: LegalEntityResponse[];
  /** Set the scope — updates context + localStorage + server preference. */
  setScope: (scope: string) => void;
  /** True while the initial entities + preference fetch is in flight. */
  isLoading: boolean;
  /** True while the entities fetch is still resolving (incl. retries). */
  entitiesLoading: boolean;
  /** True when the entities fetch errored after exhausting retries. */
  entitiesError: boolean;
  /** Re-run the entities + preference fetch (powers the gate's retry). */
  refetchEntities: () => void;
  /** True when the tenant exists but has no LegalEntity rows yet. */
  noEntities: boolean;
}

const LegalEntityContext = createContext<LegalEntityContextValue | null>(null);

function _isAll(scope: string): boolean {
  return scope === ALL_ENTITIES;
}

function _isPlausibleUuid(value: string): boolean {
  return /^[0-9a-f-]{32,36}$/i.test(value.trim());
}

export function LegalEntityProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const urlLe = searchParams.get("le") ?? "";
  const qc = useQueryClient();

  const [persistedScope, setPersistedScope] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(_LS_KEY) ?? "";
  });

  const entitiesQ = useQuery({
    queryKey: ["legal-entities"],
    queryFn: () => api.listOnboardingEntities(),
    staleTime: 60_000,
    // Un-provisioned tenants 4xx here — that's terminal, not retried.
    // Transient 5xx / network errors (a backend redeploy bouncing the
    // API) ARE retried so a deploy blip doesn't surface a hard error.
    retry: _retryTransient,
  });

  // Server-side per-user preference. The source of truth for scope.
  const prefQ = useQuery({
    queryKey: ["my-preferences"],
    queryFn: () => api.getMyPreferences(),
    staleTime: 60_000,
    retry: _retryTransient,
  });

  const entities = entitiesQ.data?.entities ?? [];
  const isLoading = entitiesQ.isLoading || prefQ.isLoading;

  // The server preference, normalized to a scope string.
  const serverScope = useMemo<string | null>(() => {
    if (prefQ.data === undefined) return null;
    return prefQ.data.active_legal_entity_id ?? ALL_ENTITIES;
  }, [prefQ.data]);

  // Resolve the effective scope following the precedence order.
  const scope = useMemo<string>(() => {
    // 1. URL ?le= deep link.
    if (urlLe && _isPlausibleUuid(urlLe)) return urlLe;
    // 2. Server preference.
    if (serverScope !== null) {
      if (_isAll(serverScope)) return ALL_ENTITIES;
      if (entities.some((e) => e.id === serverScope)) return serverScope;
      // Saved LE no longer exists — fall through.
    }
    // 3. localStorage fast-path cache.
    if (persistedScope) {
      if (_isAll(persistedScope)) return ALL_ENTITIES;
      if (entities.some((e) => e.id === persistedScope)) {
        return persistedScope;
      }
    }
    // 4. Default: ALL_ENTITIES for multi-LE tenants, the sole LE
    //    otherwise.
    if (entities.length === 1) return entities[0]!.id;
    if (entities.length > 1) return ALL_ENTITIES;
    return "";
  }, [urlLe, serverScope, persistedScope, entities]);

  // Concrete id for list pages — first entity when scope is "all".
  const legalEntityId = useMemo<string>(() => {
    if (_isAll(scope) || scope === "") {
      return entities.length > 0 ? entities[0]!.id : "";
    }
    return scope;
  }, [scope, entities]);

  // Keep localStorage in sync with the resolved scope so the next
  // page load has the fast-path value.
  useEffect(() => {
    if (scope && scope !== persistedScope) {
      try {
        window.localStorage.setItem(_LS_KEY, scope);
      } catch {
        // Storage may be unavailable (Safari private mode etc.).
      }
      setPersistedScope(scope);
    }
  }, [scope, persistedScope]);

  const setScope = useCallback((next: string) => {
    try {
      window.localStorage.setItem(_LS_KEY, next);
    } catch {
      // ignore
    }
    setPersistedScope(next);
    // CRITICAL: optimistically update the cached server preference
    // so the next render's `serverScope` reads the NEW value.
    // Without this, the resolution order (server pref > localStorage)
    // would re-derive the OLD value from the stale prefQ cache and
    // the click would silently revert. This is why "Riyadh" kept
    // snapping back to "Dammam" before — the server hadn't yet
    // returned the updated pref, and prefQ wasn't invalidated.
    qc.setQueryData<{ active_legal_entity_id: string | null } | undefined>(
      ["my-preferences"],
      (prev) => ({
        ...(prev ?? {}),
        active_legal_entity_id: _isAll(next) ? null : next,
      }),
    );
    // Persist to the server (fire-and-forget — the optimistic cache
    // write already made it feel instant). On error we don't revert
    // because the localStorage value still drives the session.
    void api
      .updateMyPreferences({
        active_legal_entity_id: _isAll(next) ? null : next,
      })
      .catch(() => {});
  }, [qc]);

  const refetchEntities = useCallback(() => {
    void entitiesQ.refetch();
    void prefQ.refetch();
  }, [entitiesQ, prefQ]);

  const value: LegalEntityContextValue = {
    scope,
    isAllScope: _isAll(scope),
    legalEntityId,
    legalEntities: entities,
    setScope,
    isLoading,
    // `isPending` stays true through the whole retry window — it only
    // settles on a successful fetch or a final (post-retry) error.
    entitiesLoading: entitiesQ.isPending,
    entitiesError: entitiesQ.isError,
    refetchEntities,
    noEntities:
      !isLoading && entities.length === 0 && !entitiesQ.isError,
  };

  return (
    <LegalEntityContext.Provider value={value}>
      {children}
    </LegalEntityContext.Provider>
  );
}

export function useLegalEntityContext(): LegalEntityContextValue {
  const ctx = useContext(LegalEntityContext);
  if (ctx === null) {
    throw new Error(
      "useLegalEntityContext must be used inside <LegalEntityProvider>",
    );
  }
  return ctx;
}

/**
 * Same context but returns null instead of throwing when no provider
 * is mounted. Used by ambient UI like the LegalEntitySwitcher that
 * lives inside the AppShell — isolated component tests render the
 * shell without the provider and we don't want them to crash.
 */
export function useOptionalLegalEntityContext():
  | LegalEntityContextValue
  | null {
  return useContext(LegalEntityContext);
}

/**
 * Convenience hook for pages that need a CONCRETE LegalEntity id
 * (their API endpoint requires `legal_entity_id`). Returns "" while
 * loading and on tenants with no LEs. When the scope is "All
 * entities", returns the first entity's id.
 */
export function useLegalEntityId(): string {
  return useLegalEntityContext().legalEntityId;
}

/**
 * Hook for pages whose API accepts an OPTIONAL legal entity (Mission
 * Control, module landing pages). Returns `ALL_ENTITIES` or a
 * concrete id. Pass `undefined` to the API when this is ALL_ENTITIES.
 */
export function useLegalEntityScope(): string {
  return useLegalEntityContext().scope;
}

/**
 * Gate hook for pages that REQUIRE a concrete LegalEntity id.
 *
 * Returns `{ legalEntityId, gate }`:
 *   - while the entities fetch is still resolving (incl. retries
 *     through a backend-redeploy blip) → `gate` is a `<Loading />`.
 *   - if the fetch errored after exhausting retries → `gate` is an
 *     `<ErrorPanel>` with a "Try again" button.
 *   - if the tenant genuinely has no LegalEntity rows → `gate` is a
 *     "complete onboarding" `<ErrorPanel>`.
 *   - once a concrete id is available → `gate` is `null` and
 *     `legalEntityId` is the id.
 *
 * Pages do `if (gate) return gate;` instead of the old
 * `if (!le) return <hard error>` — a transient API failure becomes a
 * spinner-then-recover instead of a scary "Missing ?le=" message.
 */
export function useLegalEntityGate(): {
  legalEntityId: string;
  gate: ReactElement | null;
} {
  const ctx = useLegalEntityContext();
  if (ctx.entitiesLoading) {
    return { legalEntityId: "", gate: <Loading /> };
  }
  if (ctx.entitiesError) {
    return {
      legalEntityId: "",
      gate: (
        <ErrorPanel
          message={
            "Couldn't reach the server to load your workspace — this " +
            "is usually a brief blip while the backend redeploys. " +
            "Try again in a moment."
          }
          onRetry={ctx.refetchEntities}
        />
      ),
    };
  }
  if (!ctx.legalEntityId) {
    return {
      legalEntityId: "",
      gate: (
        <ErrorPanel
          message={
            "No legal entity is set up for this tenant yet. Complete " +
            "onboarding to add one."
          }
        />
      ),
    };
  }
  return { legalEntityId: ctx.legalEntityId, gate: null };
}
